// router.js — deep-link e hash (design §8): TEMPO 1 (R2, reflect passivo) +
// TEMPO 2 (R5, takeHistoryOwnership — flag shell_router, DEFAULT OFF).
// Puro dove possibile (parseHash/buildHash: node --test), listener solo in install.
//
// Formato: #/<module>[/<item>][?k=v]  →  #/proc/suppliers?id=SUP123, #/email/inbox, #/dash
//
// TEMPO 1 (R2, default) = reflect passivo: la shell scrive l'hash SOLO con
// history.replaceState (ZERO entry nuove: il monolite resta l'unico proprietario
// della history, proc-v0.js:16700) e LEGGE l'hash al boot:
//   parseHash(location.hash) || routeStore.read() || wsh_active legacy || null
// Refresh su #/cont resta su cont (B2.16).
//
// TEMPO 2 (R5, flag shell_router='1') = takeHistoryOwnership: la shell diventa
// l'UNICO proprietario della history via hook reale window._historyReady (vedi
// il commento della funzione). Test: shell-history-owner.test.js (B2.15 L1).

const MODULE_RE = /^[a-z][a-z0-9-]*$/;

export function parseHash(hash) {
  if (typeof hash !== 'string') return null;
  const m = /^#\/([^/?#]+)(?:\/([^?#]+))?(?:\?(.*))?$/.exec(hash);
  if (!m) return null;
  const module = decodeURIComponent(m[1]);
  if (!MODULE_RE.test(module)) return null;
  const route = { module };
  if (m[2]) route.item = decodeURIComponent(m[2]);
  if (m[3]) {
    const params = {};
    let any = false;
    for (const pair of m[3].split('&')) {
      if (!pair) continue;
      const eq = pair.indexOf('=');
      const k = decodeURIComponent(eq === -1 ? pair : pair.slice(0, eq));
      const v = eq === -1 ? '' : decodeURIComponent(pair.slice(eq + 1));
      if (k) { params[k] = v; any = true; }
    }
    if (any) route.params = params;
  }
  return route;
}

export function buildHash(route) {
  if (!route || typeof route.module !== 'string') return '#/';
  let h = '#/' + encodeURIComponent(route.module);
  if (route.item) h += '/' + encodeURIComponent(route.item);
  if (route.params && typeof route.params === 'object') {
    const q = Object.keys(route.params)
      .filter((k) => route.params[k] !== undefined && route.params[k] !== null)
      .map((k) => encodeURIComponent(k) + '=' + encodeURIComponent(String(route.params[k])))
      .join('&');
    if (q) h += '?' + q;
  }
  return h;
}

// Route di boot (§8 tempo 1): hash > shell_route > wsh_active legacy > null.
export function initialRoute(win, routeStore) {
  try {
    const h = parseHash(win.location && win.location.hash);
    if (h) return h;
  } catch (e) { /* location negata: si passa alle chiavi persistite */ }
  const r = routeStore.read();
  if (r) return r;
  const legacy = routeStore.readLegacyActive();
  if (legacy) return { module: legacy };
  return null;
}

// Reflect passivo: a ogni cambio modulo/vista la shell riscrive l'hash della
// entry CORRENTE (replaceState: mai una entry nuova, mai popstate).
export function installHashReflect(deps) {
  const { win, bus, host } = deps;
  function currentRoute() {
    const m = host.currentModule();
    const route = { module: m };
    const item = host.currentItem(m);
    if (item) route.item = item;
    return route;
  }
  function reflect() {
    try {
      const h = buildHash(currentRoute());
      if (win.location.hash === h) return;
      win.history.replaceState(win.history.state, '', h);
    } catch (e) { /* history negata (iframe sandbox?): il reflect è best-effort */ }
  }
  const offs = [
    bus.on('shell:module-changed', reflect, { replay: false }),
    bus.on('shell:navigate', reflect, { replay: false }),
  ];
  return function uninstall() { offs.forEach((off) => off()); };
}

// ===========================================================================
// TEMPO 2 (R5) — presa in carico della history (flag shell_router, reversibile).
//
// SEMANTICA REALE dell'hook window._historyReady (verificata su proc-v0.js):
//  - init "3b" (proc-v0.js:16700, dentro il handler DOMContentLoaded registrato
//    a :16567 dallo script SINCRONO del monolite):
//      if (!window._historyReady) { window._historyReady = true;
//        <replaceState+pushState buffer>; addEventListener('popstate', <suo back>) }
//    → la history del monolite nasce QUI, una sola volta, a DCL.
//  - showView (proc-v0.js:16432):
//      if (!opts.isBack && window._historyReady) history.pushState({v:name},'')
//    → il pushState del monolite è vivo SOLO con la guardia alzata.
//
// Presa in carico in DUE mosse (un solo proprietario, mai due):
//  1. al boot della shell (bundle defer ⇒ esegue DOPO lo script sincrono ma PRIMA
//     dell'evento DOMContentLoaded): _historyReady = true ⇒ l'init 3b vede la
//     guardia già alzata e NON registra né buffer né popstate del monolite.
//  2. a DCL avvenuto (il nostro listener è registrato DOPO quello del monolite ⇒
//     gira a init 3b saltato e restore-vista già eseguiti): _historyReady = false
//     ⇒ il pushState di showView TACE. Da qui possiede la shell: una entry
//     (pushState con hash §8) per ogni vista/modulo NUOVO — vista ripetuta =
//     ZERO entry (B2.15 history.length stabile) — e popstate con la priorità
//     del back mobile: drawer aperto → closeMobileDrawer() · modale aperto →
//     closeModal() e si RESTA sulla vista · altrimenti applica la route dell'hash.
//
// NOTE di semantica reale (dichiarate, non nascoste):
//  - MODAL_OPEN è un `let` top-level dello script classico ⇒ NON esiste
//    window.MODAL_OPEN: l'equivalente osservabile è #modal-backdrop (creato da
//    modalShell:12208, rimosso da closeModal:12226); closeModal e
//    closeMobileDrawer invece SONO window property (function declaration
//    top-level / assegnazione esplicita :16728).
//  - il restore-vista del monolite (init "9", :16804) gira a DCL PRIMA della
//    mossa 2: il suo showView può spingere UNA entry di boot senza hash — sul
//    back quella entry si tratta come confine: si ri-spinge la route corrente
//    (parità con oggi: il back non esce mai dall'app).
//  - GATE timing (design §13.5): se _historyReady è GIÀ true quando la shell
//    parte (init 3b già girato: ordine di boot anomalo) la presa in carico si
//    ANNULLA rumorosamente e la history resta al monolite. Chi chiama fa
//    fallback su installHashReflect (comportamento identico a oggi).
// ===========================================================================
export function takeHistoryOwnership(deps) {
  const { win, bus, host } = deps;
  const logger = deps.logger || console;
  const doc = win.document;

  if (win._historyReady) {
    logger.error('[shell] shell_router: init history del monolite GIÀ eseguito — presa in carico ANNULLATA, history al monolite (comportamento odierno).');
    return null;
  }
  win._historyReady = true; // mossa 1: spegne l'init 3b del monolite (guardia proc-v0.js:16700)

  let owned = false;
  let applying = false;       // si sta applicando un popstate: MAI pushState
  const lastRawByModule = {}; // vista RAW per modulo: il detail ha la SUA entry (back → lista)
  const offs = [];

  function currentRoute() {
    const m = host.currentModule();
    const route = { module: m };
    const item = host.currentItem(m);
    if (item) route.item = item;
    return route;
  }

  function push(h, raw) {
    try { win.history.pushState({ shell: 1, v: raw || null }, '', h); } catch (e) { /* history negata: best-effort */ }
  }
  function repushCurrent() {
    push(buildHash(currentRoute()), lastRawByModule[host.currentModule()] || null);
  }

  // una entry per vista/modulo NUOVO; hash identico E stessa vista raw = niente
  // (showView ripetuto ⇒ history.length stabile, B2.15)
  function reflectOrPush(rawChanged, raw) {
    const h = buildHash(currentRoute());
    if (applying) {
      // popstate in applicazione: la entry è già quella giusta, al più si riallinea l'hash
      try { if (win.location.hash !== h) win.history.replaceState(win.history.state, '', h); } catch (e) { /* best-effort */ }
      return;
    }
    if (win.location.hash !== h || rawChanged) push(h, raw);
  }

  function onNavigate(p) {
    if (!p || p.module !== host.currentModule()) return; // moduli in background: non è la vista visibile
    const raw = p.view || p.item || null;
    const rawChanged = raw !== null && lastRawByModule[p.module] !== raw;
    if (raw !== null) lastRawByModule[p.module] = raw;
    reflectOrPush(rawChanged, raw);
  }
  function onModuleChanged() { reflectOrPush(false, null); }

  function onPopstate() {
    // priorità del back (design §8): 1) drawer mobile aperto → si chiude, la vista resta
    const drawer = doc.getElementById('nav-drawer');
    if (drawer && drawer.classList && !drawer.classList.contains('hidden')) {
      try {
        if (typeof win.closeMobileDrawer === 'function') win.closeMobileDrawer();
        else drawer.classList.add('hidden');
      } catch (e) { /* drawer già chiuso */ }
      repushCurrent(); // il back ha consumato una entry: si ripristina, niente navigazione
      return;
    }
    // 2) modale aperto → si chiude, la vista resta (#modal-backdrop ⇔ MODAL_OPEN, vedi header)
    if (doc.getElementById('modal-backdrop')) {
      try { if (typeof win.closeModal === 'function') win.closeModal(); } catch (e) { /* modale già chiuso */ }
      repushCurrent();
      return;
    }
    // 3) route: si applica l'hash della entry su cui si è atterrati
    const r = parseHash(win.location.hash);
    if (!r) { repushCurrent(); return; } // entry di boot senza route: il back non esce dall'app (parità con oggi)
    applying = true;
    try { host.navigate(r.module, r.item, r.params); } catch (e) { logger.error('[shell] applyRoute (popstate)', e); }
    applying = false;
    if (r.item) lastRawByModule[r.module] = r.item;
  }

  function own() {
    if (owned) return;
    owned = true;
    win._historyReady = false; // mossa 2: il pushState di showView (proc-v0.js:16432) da qui TACE
    try {
      // seed: la entry CORRENTE porta la route (un deep-link già nell'hash si preserva)
      const h = parseHash(win.location.hash) ? win.location.hash : buildHash(currentRoute());
      win.history.replaceState({ shell: 1, v: null }, '', h);
    } catch (e) { /* history negata: best-effort */ }
    win.addEventListener('popstate', onPopstate);
    offs.push(bus.on('shell:navigate', onNavigate, { replay: false }));
    offs.push(bus.on('shell:module-changed', onModuleChanged, { replay: false }));
    win.__shellHistoryOwner = true; // marcatore osservabile (PROOF/B2.15)
  }

  // il possesso scatta a DCL (dopo il handler del monolite); 'load' e readyState
  // 'complete' sono la rete di sicurezza se il DCL è già passato senza monolite.
  function kick() { own(); }
  if (doc.readyState === 'complete') kick();
  else {
    win.addEventListener('DOMContentLoaded', kick);
    win.addEventListener('load', kick);
  }

  return function uninstall() {
    win.removeEventListener('DOMContentLoaded', kick);
    win.removeEventListener('load', kick);
    win.removeEventListener('popstate', onPopstate);
    offs.forEach((off) => off());
    if (!owned) win._historyReady = undefined; // pre-possesso: l'hook torna al monolite
    owned = true; // kick tardivi non riprendono il possesso
    win.__shellHistoryOwner = false;
  };
}
